/**
 * Plan Ranking และ Icon System
 * จัดการการเรียงลำดับและแสดง icon ของ Plan ต่างๆ
 */

// Plan Ranking Configuration
export const planRanking = {
  customer: {
    free: { rank: 1, icon: '🥉', name: 'Free', color: 'bg-gray-100 text-gray-800', description: 'แผนเริ่มต้น' },
    basic: { rank: 2, icon: '🥈', name: 'Basic', color: 'bg-blue-100 text-blue-800', description: 'แผนพื้นฐาน' },
    silver: { rank: 3, icon: '🥇', name: 'Silver', color: 'bg-gray-100 text-gray-800', description: 'แผนเงิน' },
    gold: { rank: 4, icon: '👑', name: 'Gold', color: 'bg-yellow-100 text-yellow-800', description: 'แผนทอง' },
    platinum: { rank: 5, icon: '💎', name: 'Platinum', color: 'bg-purple-100 text-purple-800', description: 'แผนสูงสุด' }
  },
  girl: {
    free: { rank: 1, icon: '🥉', name: 'Free', color: 'bg-gray-100 text-gray-800', description: 'แผนเริ่มต้น' },
    basic: { rank: 2, icon: '🥈', name: 'Basic', color: 'bg-blue-100 text-blue-800', description: 'แผนพื้นฐาน' },
    premium: { rank: 3, icon: '🥇', name: 'Premium', color: 'bg-green-100 text-green-800', description: 'แผนพรีเมียม' },
    vip: { rank: 4, icon: '💎', name: 'VIP', color: 'bg-purple-100 text-purple-800', description: 'แผน VIP' }
  },
  momo: {
    starter: { rank: 1, icon: '🥉', name: 'Starter', color: 'bg-gray-100 text-gray-800', description: 'แผนเริ่มต้น' },
    basic: { rank: 2, icon: '🥈', name: 'Basic', color: 'bg-blue-100 text-blue-800', description: 'แผนพื้นฐาน' },
    pro: { rank: 3, icon: '🥇', name: 'Pro', color: 'bg-green-100 text-green-800', description: 'แผนโปร' },
    enterprise: { rank: 4, icon: '👑', name: 'Enterprise', color: 'bg-purple-100 text-purple-800', description: 'แผนองค์กร' },
    ultraEnterprise: { rank: 5, icon: '💎', name: 'Ultra Enterprise', color: 'bg-red-100 text-red-800', description: 'แผนองค์กรขั้นสูง' }
  }
};

/**
 * ดึงข้อมูล ranking ของ plan
 * @param {string} role - role ของผู้ใช้ (customer, girl, momo)
 * @param {string} planKey - key ของ plan
 * @returns {object|null} - ข้อมูล ranking หรือ null
 */
export const getPlanRanking = (role, planKey) => {
  if (!planRanking[role] || !planRanking[role][planKey]) {
    return null;
  }
  return planRanking[role][planKey];
};

/**
 * เรียง plan configurations ตาม rank
 * @param {Array} planConfigs - รายการ plan configurations
 * @param {string} role - role ของผู้ใช้
 * @returns {Array} - plan configurations ที่เรียงตาม rank
 */
export const getSortedPlansByRank = (planConfigs, role) => {
  return planConfigs.sort((a, b) => {
    const aRank = getPlanRanking(role, a.key)?.rank || 999;
    const bRank = getPlanRanking(role, b.key)?.rank || 999;
    return aRank - bRank;
  });
};

/**
 * สร้าง dropdown options สำหรับ plan selection
 * @param {string} role - role ของผู้ใช้
 * @returns {Array} - dropdown options
 */
export const getPlanDropdownOptions = (role) => {
  const plans = planRanking[role] || {};
  return Object.entries(plans).map(([key, plan]) => ({
    value: key,
    label: `${plan.icon} ${plan.name}`,
    description: plan.description
  }));
};

/**
 * สร้าง plan badge component
 * @param {string} role - role ของผู้ใช้
 * @param {string} planKey - key ของ plan
 * @returns {object} - badge configuration
 */
export const createPlanBadge = (role, planKey) => {
  const ranking = getPlanRanking(role, planKey);
  if (!ranking) {
    return {
      icon: '❓',
      name: 'Unknown',
      color: 'bg-gray-100 text-gray-800',
      rank: 0
    };
  }
  return ranking;
};

/**
 * ดึงข้อมูล plan ทั้งหมดสำหรับ role
 * @param {string} role - role ของผู้ใช้
 * @returns {Array} - รายการ plans
 */
export const getPlansByRole = (role) => {
  const plans = planRanking[role] || {};
  return Object.entries(plans).map(([key, plan]) => ({
    key,
    ...plan
  }));
};

/**
 * ตรวจสอบว่า plan มีอยู่หรือไม่
 * @param {string} role - role ของผู้ใช้
 * @param {string} planKey - key ของ plan
 * @returns {boolean} - true ถ้ามี plan นี้
 */
export const isValidPlan = (role, planKey) => {
  return !!(planRanking[role] && planRanking[role][planKey]);
};

/**
 * ดึง plan ที่มี rank สูงสุด
 * @param {string} role - role ของผู้ใช้
 * @returns {object|null} - plan ที่มี rank สูงสุด
 */
export const getHighestRankPlan = (role) => {
  const plans = planRanking[role];
  if (!plans) return null;
  
  let highestRank = 0;
  let highestPlan = null;
  
  Object.entries(plans).forEach(([key, plan]) => {
    if (plan.rank > highestRank) {
      highestRank = plan.rank;
      highestPlan = { key, ...plan };
    }
  });
  
  return highestPlan;
};

/**
 * ดึง plan ที่มี rank ต่ำสุด
 * @param {string} role - role ของผู้ใช้
 * @returns {object|null} - plan ที่มี rank ต่ำสุด
 */
export const getLowestRankPlan = (role) => {
  const plans = planRanking[role];
  if (!plans) return null;
  
  let lowestRank = 999;
  let lowestPlan = null;
  
  Object.entries(plans).forEach(([key, plan]) => {
    if (plan.rank < lowestRank) {
      lowestRank = plan.rank;
      lowestPlan = { key, ...plan };
    }
  });
  
  return lowestPlan;
};

/**
 * ดึง plan ที่มี rank ถัดไป
 * @param {string} role - role ของผู้ใช้
 * @param {string} currentPlanKey - plan ปัจจุบัน
 * @returns {object|null} - plan ถัดไป
 */
export const getNextPlan = (role, currentPlanKey) => {
  const currentRank = getPlanRanking(role, currentPlanKey)?.rank;
  if (!currentRank) return null;
  
  const plans = planRanking[role];
  if (!plans) return null;
  
  let nextPlan = null;
  let nextRank = 999;
  
  Object.entries(plans).forEach(([key, plan]) => {
    if (plan.rank > currentRank && plan.rank < nextRank) {
      nextRank = plan.rank;
      nextPlan = { key, ...plan };
    }
  });
  
  return nextPlan;
};

/**
 * ดึง plan ที่มี rank ก่อนหน้า
 * @param {string} role - role ของผู้ใช้
 * @param {string} currentPlanKey - plan ปัจจุบัน
 * @returns {object|null} - plan ก่อนหน้า
 */
export const getPreviousPlan = (role, currentPlanKey) => {
  const currentRank = getPlanRanking(role, currentPlanKey)?.rank;
  if (!currentRank) return null;
  
  const plans = planRanking[role];
  if (!plans) return null;
  
  let previousPlan = null;
  let previousRank = 0;
  
  Object.entries(plans).forEach(([key, plan]) => {
    if (plan.rank < currentRank && plan.rank > previousRank) {
      previousRank = plan.rank;
      previousPlan = { key, ...plan };
    }
  });
  
  return previousPlan;
};









